import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import {
  CreditTransactionDetailModal,
  NumberedPagination,
  PortalUsdLedgerSummaryGrid,
  PortalUsdWalletLedgerTable,
  UsdWalletTransactionDetailModal,
  computeUsdLedgerSummary,
  cycleSortState,
  ViewTransactionButton,
  usePortalLedgerDetailState,
  type SortState,
  type UsdLedgerRowLike,
} from '@kioskgaming/ui';
import {
  fetchManagedShopCdnTransactions,
  fetchManagedShopCreditTransactionById,
  fetchManagedShopUsdLedger,
  fetchManagedShopUsdLedgerEntryById,
} from '../../services/api';
import type { AgentWalletLedgerEntry, ManagedShopUsdLedgerData } from '../../types';
import {
  formatSignedAmount,
  ledgerDescriptionDisplay,
  signedChange,
  walletInOutLabel,
} from '../../utils/agentWalletLedger';
import { formatWalletLedgerDate } from '../../utils/formatDate';

type WalletTab = 'credits' | 'usd';
type CdnSortKey = 'date' | 'change' | 'balance';

function fmtNum(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}

function formatLedgerBalanceCell(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
}

function txDateMs(tx: AgentWalletLedgerEntry): number {
  const raw = (tx as { createdAt?: string; created_at?: string }).createdAt
    ?? (tx as { created_at?: string }).created_at;
  const t = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
}

export default function ManagedShopWalletHistoryPanel({ shopId }: { shopId: string }) {
  const [tab, setTab] = useState<WalletTab>('credits');
  const [cdnItems, setCdnItems] = useState<AgentWalletLedgerEntry[]>([]);
  const [cdnPage, setCdnPage] = useState(1);
  const [cdnPagination, setCdnPagination] = useState<{ totalPages: number; total: number } | null>(null);
  const [cdnLoading, setCdnLoading] = useState(false);
  const [cdnSort, setCdnSort] = useState<{ by: CdnSortKey; dir: 'asc' | 'desc' }>({ by: 'date', dir: 'desc' });

  const [usdData, setUsdData] = useState<ManagedShopUsdLedgerData | null>(null);
  const [usdPage, setUsdPage] = useState(1);
  const [usdLoading, setUsdLoading] = useState(false);
  const [usdSort, setUsdSort] = useState<SortState>({ sortBy: 'createdAt', sortOrder: 'DESC' });

  useEffect(() => {
    setTab('credits');
    setCdnPage(1);
    setUsdPage(1);
  }, [shopId]);

  const cdnLimit = 25;
  const usdLimit = 25;

  const usdApiSort = useMemo(() => {
    const by = usdSort.sortBy;
    const mapped =
      by === 'amount'
        ? 'amount'
        : by === 'balanceAfter'
          ? 'balance_after'
          : by === 'balanceBefore'
            ? 'balance_before'
            : 'created';
    return {
      sort: mapped,
      sort_dir: (usdSort.sortOrder === 'ASC' ? 'asc' : 'desc') as 'asc' | 'desc',
    };
  }, [usdSort]);

  const loadCdn = useCallback(async () => {
    setCdnLoading(true);
    try {
      const res = await fetchManagedShopCdnTransactions(shopId, { page: cdnPage, limit: cdnLimit });
      if (!res.success) {
        toast.error(res.message || 'Could not load credit history');
        setCdnItems([]);
        setCdnPagination(null);
        return;
      }
      setCdnItems(res.data?.items ?? []);
      const p = res.data?.pagination;
      setCdnPagination(p ? { totalPages: p.totalPages, total: p.total } : null);
    } catch {
      toast.error('Could not load credit history');
      setCdnItems([]);
      setCdnPagination(null);
    } finally {
      setCdnLoading(false);
    }
  }, [shopId, cdnPage]);

  const loadUsd = useCallback(async () => {
    setUsdLoading(true);
    try {
      const res = await fetchManagedShopUsdLedger(shopId, {
        page: usdPage,
        limit: usdLimit,
        sort: usdApiSort.sort,
        sort_dir: usdApiSort.sort_dir,
      });
      if (!res.success) {
        toast.error(res.message || 'Could not load USD history');
        setUsdData(null);
        return;
      }
      setUsdData(res.data ?? null);
    } catch {
      toast.error('Could not load USD history');
      setUsdData(null);
    } finally {
      setUsdLoading(false);
    }
  }, [shopId, usdPage, usdApiSort]);

  useEffect(() => {
    if (tab === 'credits') void loadCdn();
  }, [tab, loadCdn]);

  useEffect(() => {
    if (tab === 'usd') void loadUsd();
  }, [tab, loadUsd]);

  const sortedCdnItems = useMemo(() => {
    const dir = cdnSort.dir === 'asc' ? 1 : -1;
    return [...cdnItems].sort((a, b) => {
      let cmp = 0;
      if (cdnSort.by === 'change') {
        cmp = Math.abs(signedChange(a)) - Math.abs(signedChange(b));
      } else if (cdnSort.by === 'balance') {
        cmp = Math.abs(Number(a.balanceAfter) || 0) - Math.abs(Number(b.balanceAfter) || 0);
      } else {
        cmp = txDateMs(a) - txDateMs(b);
      }
      return cmp * dir;
    });
  }, [cdnItems, cdnSort]);

  const usdRows = useMemo(() => usdData?.rows ?? [], [usdData?.rows]);
  const usdPagination = usdData?.pagination;
  const usdSummary = useMemo(
    () => computeUsdLedgerSummary((usdRows as UsdLedgerRowLike[]) ?? []),
    [usdRows],
  );

  const ledgerDetail = usePortalLedgerDetailState({
    creditRows: cdnItems,
    usdRows: usdRows as UsdLedgerRowLike[],
    fetchCreditById: async (id) => {
      const res = await fetchManagedShopCreditTransactionById(shopId, id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    fetchUsdById: async (id) => {
      const res = await fetchManagedShopUsdLedgerEntryById(shopId, id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    onUnavailable: () =>
      toast.error('Related transaction is unavailable or outside your permission.'),
  });

  const toggleCdnSort = (by: CdnSortKey) => {
    setCdnSort((prev) =>
      prev.by === by ? { by, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { by, dir: 'desc' },
    );
  };

  const cdnSortMark = (by: CdnSortKey) =>
    cdnSort.by === by ? (cdnSort.dir === 'asc' ? '↑' : '↓') : '↕';

  return (
    <div className="mt-8">
      <h2 className="mb-3 text-sm font-semibold text-gray-900">Wallet history</h2>
      <div
        className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50/80 p-1 sm:inline-flex"
        role="tablist"
        aria-label="Wallet history type"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'credits'}
          onClick={() => setTab('credits')}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${tab === 'credits' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
        >
          Credits
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'usd'}
          onClick={() => setTab('usd')}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${tab === 'usd' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
        >
          USD wallet
        </button>
      </div>

      {tab === 'credits' && (
        <div className="mt-4 overflow-hidden rounded-xl border border-orange-100 bg-white shadow-sm">
          {cdnLoading ? (
            <div className="p-8">
              <PageLoadingIndicator variant="inline" label="Loading credit history…" />
            </div>
          ) : sortedCdnItems.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">No credit transactions yet.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50/80">
                    <tr>
                      <th className="px-4 py-3 font-semibold text-gray-700">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900" onClick={() => toggleCdnSort('date')}>
                          Date <span className="text-[10px] text-gray-400">{cdnSortMark('date')}</span>
                        </button>
                      </th>
                      <th className="px-4 py-3 font-semibold text-gray-700">In / out</th>
                      <th className="px-4 py-3 font-semibold text-gray-700 text-right">
                        <button type="button" className="inline-flex w-full items-center justify-end gap-1 hover:text-gray-900" onClick={() => toggleCdnSort('change')}>
                          Change <span className="text-[10px] text-gray-400">{cdnSortMark('change')}</span>
                        </button>
                      </th>
                      <th className="px-4 py-3 font-semibold text-gray-700 text-right">
                        <button type="button" className="inline-flex w-full items-center justify-end gap-1 hover:text-gray-900" onClick={() => toggleCdnSort('balance')}>
                          Balance after <span className="text-[10px] text-gray-400">{cdnSortMark('balance')}</span>
                        </button>
                      </th>
                      <th className="px-4 py-3 font-semibold text-gray-700">Description</th>
                      <th className="px-4 py-3 font-semibold text-gray-700 text-right"> </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedCdnItems.map((tx) => {
                      const delta = signedChange(tx);
                      const io = walletInOutLabel(tx);
                      return (
                        <tr key={tx.id} className="hover:bg-gray-50/50">
                          <td className="whitespace-nowrap px-4 py-3 text-gray-700">{formatWalletLedgerDate(tx)}</td>
                          <td
                            className={`px-4 py-3 font-semibold ${io === 'IN' ? 'text-emerald-700' : io === 'OUT' ? 'text-red-700' : 'text-gray-500'
                              }`}
                          >
                            {io}
                          </td>
                          <td
                            className={`px-4 py-3 text-right font-medium tabular-nums ${delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-red-700' : 'text-gray-800'
                              }`}
                          >
                            {formatSignedAmount(delta)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                            {formatLedgerBalanceCell(tx.balanceAfter)}
                          </td>
                          <td className="max-w-[14rem] px-4 py-3 text-xs text-gray-600 break-words sm:max-w-md">
                            {ledgerDescriptionDisplay(tx)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <ViewTransactionButton onClick={() => ledgerDetail.openCredit(tx)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {cdnPagination && cdnPagination.totalPages > 1 && (
                <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 sm:flex-row">
                  <p className="text-sm text-gray-500">
                    Page {cdnPage} of {cdnPagination.totalPages} · {cdnPagination.total} total
                  </p>
                  <NumberedPagination
                    currentPage={cdnPage}
                    totalPages={cdnPagination.totalPages}
                    onPageChange={setCdnPage}
                    disabled={cdnLoading}
                    variant="portal"
                    className="justify-center sm:justify-end"
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'usd' && (
        <div className="mt-4 overflow-hidden rounded-xl border border-orange-100 bg-white shadow-sm">
          {usdData && (
            <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-3 text-sm">
              <span className="text-gray-600">USD net balance: </span>
              <span className="font-semibold tabular-nums text-gray-900">{fmtNum(usdData.balanceNet)}</span>
              <span className="mx-2 text-gray-300">|</span>
              <span className="text-gray-600">Gross: </span>
              <span className="font-medium tabular-nums text-gray-800">{fmtNum(usdData.balanceGross)}</span>
            </div>
          )}
          {usdLoading ? (
            <div className="p-8">
              <PageLoadingIndicator variant="inline" label="Loading USD history…" />
            </div>
          ) : usdRows.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">No USD wallet transactions yet.</p>
          ) : (
            <>
              <div className="border-b border-gray-100 px-4 py-4">
                <PortalUsdLedgerSummaryGrid summary={usdSummary} loading={usdLoading} />
              </div>
              <PortalUsdWalletLedgerTable
                rows={usdRows as UsdLedgerRowLike[]}
                sortable
                sortActiveKey={usdSort.sortBy}
                sortDirection={usdSort.sortOrder}
                onSort={(key) => {
                  setUsdSort((prev) => cycleSortState(prev, key, 'DESC'));
                  setUsdPage(1);
                }}
                onViewDetail={(row) => {
                  const id = row.id != null ? String(row.id) : '';
                  if (id) void ledgerDetail.openUsdById(id);
                }}
              />
              {usdPagination && usdPagination.totalPages > 1 && (
                <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 sm:flex-row">
                  <p className="text-sm text-gray-500">
                    Page {usdPage} of {usdPagination.totalPages} · {usdPagination.total} total
                  </p>
                  <NumberedPagination
                    currentPage={usdPage}
                    totalPages={usdPagination.totalPages}
                    onPageChange={setUsdPage}
                    disabled={usdLoading}
                    variant="portal"
                    className="justify-center sm:justify-end"
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <CreditTransactionDetailModal
        entry={ledgerDetail.creditDetail}
        onClose={ledgerDetail.closeCredit}
        accountLabel="Shop credit wallet"
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
        resolveUsdConversion={ledgerDetail.resolveUsdConversion}
      />
      <UsdWalletTransactionDetailModal
        row={ledgerDetail.usdDetail}
        onClose={ledgerDetail.closeUsd}
        onOpenCreditSource={ledgerDetail.handleOpenCreditSource}
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
      />
    </div>
  );
}
